import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import HeroSection from './components/HeroSection';
import ProvincesSection from './components/ProvincesSection';
import ServicesSection from './components/ServicesSection';
import AdvantagesSection from './components/AdvantagesSection';
import ProcessSection from './components/ProcessSection';
import CasesSection from './components/CasesSection';
import FooterSection from './components/FooterSection';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: '猎鹰侦探事务所',
  description: '专业提供出轨调查、婚外情取证、找人寻人、商业调查、员工背景调查等服务，合法合规，全国300+城市覆盖。',
  url: siteUrl,
  telephone: '400-000-0000',
  areaServed: 'CN',
  serviceType: ['出轨调查', '婚外情调查', '找人公司', '寻人公司', '商业调查', '背景调查'],
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '00:00',
      closes: '23:59',
    },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: '调查服务',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '出轨调查' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '婚外情取证' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '找人寻人' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '商业调查' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '员工背景调查' } },
    ],
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '私家侦探合法吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '猎鹰侦探事务所依法合规经营，所有调查行为严格遵守《民事诉讼法》及相关法律法规，调查取证合法有效，可作为法庭证据使用。',
      },
    },
    {
      '@type': 'Question',
      name: '出轨调查费用是多少？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '出轨调查收费标准根据案件复杂程度、调查周期、地域范围等因素综合评估，我们提供免费咨询和透明报价，无隐藏费用。',
      },
    },
    {
      '@type': 'Question',
      name: '找人公司靠谱吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '猎鹰侦探拥有15年专业经验，5000+成功案例，团队由前公安、法律专家组成，找人成功率高，全程保密，绝不骗人。',
      },
    },
    {
      '@type': 'Question',
      name: '调查取证违法吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '合法的调查取证不违法。我们严格在法律框架内开展调查，不侵犯他人隐私权，所获证据符合法律规定，可在诉讼中使用。',
      },
    },
    {
      '@type': 'Question',
      name: '如何调查配偶出轨？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '建议委托专业出轨调查公司进行，猎鹰侦探提供跟踪取证、行为记录、证据固定等全套服务，帮助您合法收集出轨证据。',
      },
    },
  ],
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '猎鹰侦探-专业出轨调查·找人公司·商业调查|全国服务',
  description: '猎鹰侦探事务所提供专业出轨调查、婚外情取证、找人寻人、商业背调等服务。私家侦探合法合规，调查取证有效，出轨调查费用透明，全国300+城市覆盖，15年经验，免费咨询。',
  url: siteUrl,
  inLanguage: 'zh-CN',
  isPartOf: {
    '@type': 'WebSite',
    name: '猎鹰侦探事务所',
    url: siteUrl,
  },
};

const SchemaJsonLd = () => (
  <>
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }}
    />
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
    />
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
    />
  </>
);

const CtaSection = () => (
  <section id="contact" className="py-16 bg-[#0a1209] relative overflow-hidden">
    {/* Glow */}
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div className="w-[500px] h-[200px] bg-[#c9a84c]/8 rounded-full blur-3xl"></div>
    </div>
    {/* Top/bottom border lines */}
    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent"></div>
    <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent"></div>

    <div className="max-w-3xl mx-auto px-4 text-center relative z-10">
      <div className="inline-flex items-center gap-2 bg-[#c9a84c]/10 border border-[#c9a84c]/25 rounded-full px-4 py-1.5 mb-5">
        <span className="w-1.5 h-1.5 rounded-full bg-[#c9a84c] inline-block animate-pulse"></span>
        <span className="text-[#c9a84c] text-xs font-semibold tracking-widest uppercase">24H Online</span>
      </div>
      <h2 className="text-2xl md:text-3xl font-black text-white mb-3">需要专业调查服务？</h2>
      <p className="text-white/45 text-sm mb-8">猎鹰侦探提供专业、保密的调查服务，帮助您解决困扰</p>
      <a
        href="tel:400-000-0000"
        className="relative inline-flex items-center gap-2 overflow-hidden bg-[#c9a84c] hover:bg-[#b8963e] text-[#0f1a14] font-black px-10 py-3.5 rounded-md text-base transition-colors cursor-pointer whitespace-nowrap group"
      >
        <i className="ri-phone-line relative z-10"></i>
        <span className="relative z-10">免费咨询调查方案</span>
        <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"></span>
      </a>
    </div>
  </section>
);

const SeoSection = () => (
  <section className="py-12 bg-[#060e08] border-t border-[#c9a84c]/8">
    <div className="max-w-5xl mx-auto px-4 md:px-6">

      {/* Main heading */}
      <h2 className="text-sm font-bold text-white/55 mb-1 text-center">猎鹰侦探事务所 — 专业调查公司 · 全国服务</h2>
      <p className="text-xs text-center text-[#c9a84c]/50 mb-8 tracking-widest">
        出轨调查 · 婚外情取证 · 找人寻人 · 商业背调 · 私家侦探
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs text-white/22 leading-relaxed">

        {/* Block 1: 出轨调查 */}
        <div>
          <h3 className="text-white/40 font-semibold mb-2 text-xs">
            <a id="section-chugui" className="text-white/40">出轨调查 / 婚外情调查取证</a>
          </h3>
          <p>
            猎鹰侦探是国内专业的<strong className="text-white/35 font-medium">出轨调查公司</strong>，提供婚外情调查、婚姻调查取证、出轨证据收集等全套服务。
            如果您正在烦恼"<strong className="text-white/35 font-medium">老公出轨怎么办</strong>"或"<strong className="text-white/35 font-medium">老婆出轨怎么办</strong>"，
            我们的专业团队可帮助您合法收集出轨证据，证据可直接用于离婚诉讼。
            <strong className="text-white/35 font-medium">婚外情取证合法吗？</strong>答案是肯定的——在法律框架内进行的调查取证完全合法有效。
            出轨调查费用根据案件情况透明报价，无隐藏收费。
          </p>
          <p className="mt-2">
            服务城市：<strong className="text-white/35 font-medium">北京出轨调查</strong>、<strong className="text-white/35 font-medium">上海出轨调查</strong>、
            <strong className="text-white/35 font-medium">广州出轨调查</strong>、<strong className="text-white/35 font-medium">深圳出轨调查</strong>、
            <strong className="text-white/35 font-medium">杭州出轨调查</strong>，以及朝阳区、浦东新区等细分区域均可覆盖。
          </p>
        </div>

        {/* Block 2: 找人业务 */}
        <div>
          <h3 className="text-white/40 font-semibold mb-2 text-xs">
            <a id="section-zhaoren" className="text-white/40">找人公司 / 寻人公司</a>
          </h3>
          <p>
            猎鹰侦探是国内口碑领先的<strong className="text-white/35 font-medium">找人公司</strong>，专业处理找失联人员、找欠钱的人、找老赖、找朋友联系方式等各类寻人需求。
            很多客户会问"<strong className="text-white/35 font-medium">找人公司靠谱吗</strong>"——我们用15年经验和5000+成功案例来回答。
            "<strong className="text-white/35 font-medium">怎么找一个人下落</strong>"是常见困扰，我们通过合法渠道精准定位，
            "<strong className="text-white/35 font-medium">找人一般多少钱</strong>"则根据案件难度透明报价。
          </p>
          <p className="mt-2">
            重点服务城市：<strong className="text-white/35 font-medium">北京找人公司</strong>、<strong className="text-white/35 font-medium">上海寻人公司</strong>，
            同时覆盖全国300+城市，无论对方身在何处，我们都能协助定位。
          </p>
        </div>

        {/* Block 3: 商业调查 */}
        <div>
          <h3 className="text-white/40 font-semibold mb-2 text-xs">
            <a id="section-shangye" className="text-white/40">商业调查 / 背景调查</a>
          </h3>
          <p>
            作为专业的<strong className="text-white/35 font-medium">商业调查公司</strong>，猎鹰侦探提供员工背景调查、商业信息调查、同业客户信息调查等企业级服务。
            我们的<strong className="text-white/35 font-medium">背调公司</strong>团队由前公安、法律专家组成，
            帮助企业规避用人风险、识别商业欺诈、保护核心商业利益。
            无论是招聘前的<strong className="text-white/35 font-medium">员工背景调查</strong>，还是合作前的商业尽调，我们均可提供专业支持。
          </p>
        </div>

        {/* Block 4: 价格与信任 */}
        <div>
          <h3 className="text-white/40 font-semibold mb-2 text-xs">
            <a id="section-price-trust" className="text-white/40">收费标准 / 合法合规说明</a>
          </h3>
          <p>
            关于"<strong className="text-white/35 font-medium">私家侦探多少钱</strong>"——猎鹰侦探坚持透明收费，
            <strong className="text-white/35 font-medium">出轨调查收费标准</strong>、<strong className="text-white/35 font-medium">找人公司费用</strong>、
            调查公司价格均在咨询时明确告知，无隐藏费用。
            "<strong className="text-white/35 font-medium">私家侦探合法吗</strong>"——是的，我们依法合规经营。
            "<strong className="text-white/35 font-medium">调查公司靠谱吗</strong>"——15年口碑、真实案例、签约保障，让您放心委托。
            "<strong className="text-white/35 font-medium">找人公司会不会骗人</strong>"——我们承诺先签合同、明确目标、按效果付费，保障您的权益。
          </p>
        </div>
      </div>

      {/* Keyword tag cloud */}
      <div className="mt-8 flex flex-wrap gap-2 justify-center">
        {[
          '出轨调查','婚外情调查','婚姻调查取证','出轨证据收集','出轨调查费用',
          '找人公司','寻人公司','找失联人员','找老赖','北京找人公司','上海寻人公司',
          '商业调查公司','员工背景调查','背调公司','商业信息调查',
          '私家侦探合法吗','调查公司靠谱吗','私家侦探多少钱',
          '北京出轨调查','上海出轨调查','广州出轨调查','深圳出轨调查','杭州出轨调查',
        ].map((kw) => (
          <span key={kw} className="text-[10px] text-white/18 border border-white/8 rounded px-2 py-0.5 hover:text-white/30 hover:border-white/15 transition-colors cursor-default">
            {kw}
          </span>
        ))}
      </div>
    </div>
  </section>
);

const BackToTop = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className={`fixed bottom-20 right-5 md:bottom-8 md:right-8 z-50 w-10 h-10 flex items-center justify-center bg-[#c9a84c] hover:bg-[#b8963e] text-[#0f1a14] rounded-full cursor-pointer transition-all duration-300 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
      aria-label="Back to top"
    >
      <i className="ri-arrow-up-line text-lg font-bold"></i>
    </button>
  );
};

const FloatingConsult = () => {
  const [visible, setVisible] = useState(false);
  const [pulse, setPulse] = useState(true);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 200);
    window.addEventListener('scroll', onScroll);
    // Stop pulse after 4s
    const t = setTimeout(() => setPulse(false), 4000);
    return () => { window.removeEventListener('scroll', onScroll); clearTimeout(t); };
  }, []);

  return (
    <a
      href="#contact"
      className={`fixed bottom-5 right-5 md:bottom-8 md:right-20 z-50 flex items-center gap-2 bg-[#c9a84c] hover:bg-[#b8963e] text-[#0f1a14] font-black text-sm px-4 py-2.5 rounded-full cursor-pointer whitespace-nowrap transition-all duration-300 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      {pulse && (
        <span className="absolute inset-0 rounded-full bg-[#c9a84c] animate-ping opacity-40 pointer-events-none"></span>
      )}
      <i className="ri-customer-service-2-line text-base relative z-10"></i>
      <span className="relative z-10">免费咨询</span>
    </a>
  );
};

const HomePage = () => (
  <div className="min-h-screen font-sans bg-[#0a1209]">
    <SchemaJsonLd />
    <Navbar />
    <HeroSection />
    <ProvincesSection />
    <ServicesSection />
    <AdvantagesSection />
    <ProcessSection />
    <CasesSection />
    <CtaSection />
    <SeoSection />
    <FooterSection />
    <BackToTop />
    <FloatingConsult />
  </div>
);

export default HomePage;
